import type { SketchPaletteColorOption } from "@/types/stage-types.js";

export interface ColorSelectorConfig {
  readonly options: readonly SketchPaletteColorOption[];
  readonly initialColor?: string;
  readonly onSelect?: (option: SketchPaletteColorOption) => void;
}

export interface ColorSelectorAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelectedColor: (color: string) => void;
  readonly destroy: () => void;
}

export const createColorSelector = (
  container: HTMLElement,
  config: ColorSelectorConfig
): ColorSelectorAPI => {
  const root = document.createElement("div");
  root.className = "sketch-tool-palette__colors";
  container.appendChild(root);

  let isDestroyed = false;
  let selectedColor: string | null = null;
  const buttons = new Map<string, HTMLButtonElement>();

  const setSelectedColor = (color: string): void => {
    selectedColor = color;
    buttons.forEach((button, value) => {
      const isSelected = value === color;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", String(isSelected));
    });
  };

  const handleSelect = (option: SketchPaletteColorOption): void => {
    if (isDestroyed) return;
    setSelectedColor(option.color);
    config.onSelect?.(option);
  };

  const createColorButton = (
    option: SketchPaletteColorOption
  ): HTMLButtonElement => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "sketch-tool-palette__color";
    button.style.setProperty("--sketch-tool-palette-color", option.color);
    button.dataset.color = option.color;
    button.dataset.colorId = option.id;
    button.title = option.label ?? option.id;
    button.setAttribute("aria-label", option.label ?? option.id);
    button.addEventListener("click", () => handleSelect(option));
    return button;
  };

  for (const option of config.options) {
    const button = createColorButton(option);
    buttons.set(option.color, button);
    root.appendChild(button);
  }

  const fallbackColor =
    config.initialColor && buttons.has(config.initialColor)
      ? config.initialColor
      : config.options[0]?.color;

  if (fallbackColor) {
    setSelectedColor(fallbackColor);
  }

  const getElement = (): HTMLElement => root;

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    buttons.clear();
    root.innerHTML = "";
    if (root.parentElement === container) {
      container.removeChild(root);
    }
  };

  return {
    getElement,
    setSelectedColor,
    destroy,
  };
};
